import { tripFeatureManager } from '../../../services/trip-feature-manager.js'
import { layerGroupManager } from '../../../services/layer-group-manager.js'
import { pxToMarkerSize } from '../marker-editor-dropdown.js'
import { db } from '../../../database/schema.js'
import type { Feature, FeatureLabel } from '../../../schemas/feature.js'
import type { BaseShape } from '../shapes/base-shape.js'

export interface StyleEditorCallbacks {
  onStateChanged: () => void
  getShapeComponent: (featureId: string) => BaseShape | undefined
  getSelectedFeatures: () => Feature[]
  updateSelectedFeatures: () => Promise<void>
  getHostBoundingRect: () => DOMRect
}

export class StyleEditorManager {
  // Set when a dropdown is closed via outside click to prevent map selection clearing
  dropdownJustClosed = false

  // Color picker state
  colorPickerOpen = false
  colorPickerProperty: string | undefined
  colorPickerFeatureId: string | undefined
  colorPickerColor = '#3b82f6'
  colorPickerOpacity = 1
  colorPickerPattern = 'solid'

  // Dropdown position
  dropdownTop = 68
  dropdownLeft = 0

  // Stroke style state
  strokeStyleOpen = false
  strokeStyleProperty: string | undefined
  strokeStyleFeatureId: string | undefined
  strokeStyleSelected = 'solid'

  // Stroke editor state
  strokeEditorOpen = false
  strokeEditorFeatureId: string | undefined
  strokeEditorIsLine = false
  strokeEditorColor = '#3f5a7a'
  strokeEditorOpacity = 1
  strokeEditorWidth = 3
  strokeEditorPattern = 'solid'

  // Marker editor state
  markerEditorOpen = false
  markerEditorFeatureId: string | undefined
  markerEditorType = 'circle'
  markerEditorIcon = ''
  markerEditorSize = 'xs'
  markerEditorPosition = 'center'

  // Label editor state
  labelEditorOpen = false
  labelEditorFeatureId: string | undefined
  labelEditorText = ''
  labelEditorColor = '#ffffff'
  labelEditorVisible = true
  labelEditorVerticalAlign: 'top' | 'middle' | 'bottom' = 'bottom'
  labelEditorHorizontalAlign: 'left' | 'center' | 'right' = 'center'
  labelEditorFontSize = 11
  labelEditorFontFamily = 'sans-serif'

  // Tag editor state
  tagEditorOpen = false
  tagEditorFeatureId: string | undefined
  tagEditorTags: string[] = []

  // Layer picker state
  layerPickerOpen = false
  layerPickerFeatureId: string | undefined

  constructor(private callbacks: StyleEditorCallbacks) {}

  get isAnyDropdownOpen(): boolean {
    return (
      this.colorPickerOpen ||
      this.strokeStyleOpen ||
      this.strokeEditorOpen ||
      this.markerEditorOpen ||
      this.labelEditorOpen ||
      this.tagEditorOpen ||
      this.layerPickerOpen
    )
  }

  private positionDropdown(buttonRect?: DOMRect): void {
    if (!buttonRect) {
      return
    }
    const host = this.callbacks.getHostBoundingRect()
    const dropdownWidth = 264
    const padding = 8
    this.dropdownTop = buttonRect.bottom - host.top + 4
    let left = buttonRect.left - host.left
    const maxLeft = host.width - dropdownWidth - padding
    if (left > maxLeft) {
      left = Math.max(padding, maxLeft)
    }
    this.dropdownLeft = left
  }

  private _outsideClickHandler = (e: MouseEvent | TouchEvent): void => {
    if (!this.isAnyDropdownOpen) {
      return
    }
    const path = e.composedPath()
    const isInside = path.some(
      el =>
        el instanceof HTMLElement &&
        (el.tagName === 'COLOR-PICKER-DROPDOWN' ||
          el.tagName === 'STROKE-STYLE-DROPDOWN' ||
          el.tagName === 'STROKE-EDITOR-DROPDOWN' ||
          el.tagName === 'MARKER-EDITOR-DROPDOWN' ||
          el.tagName === 'LABEL-EDITOR-DROPDOWN' ||
          el.tagName === 'TAG-EDITOR-DROPDOWN' ||
          el.tagName === 'LAYER-PICKER-DROPDOWN' ||
          el.tagName === 'GROUPED-SELECT' ||
          el.tagName === 'CAIRN-CONTEXT-BAR' ||
          el.tagName === 'EDITING-TOOLS-BAR')
    )
    if (!isInside) {
      this.dropdownJustClosed = true
      setTimeout(() => {
        this.dropdownJustClosed = false
      }, 0)
      this.closeColorPicker()
      this.closeStrokeStyle()
      this.closeStrokeEditor()
      this.closeMarkerEditor()
      this.closeLabelEditor()
      this.closeTagEditor()
      this.closeLayerPicker()
    }
  }

  startOutsideClickListener(): void {
    // Remove first to prevent duplicate listeners when switching between dropdowns
    this.stopOutsideClickListener()
    document.addEventListener('click', this._outsideClickHandler, true)
    document.addEventListener('touchend', this._outsideClickHandler, true)
  }

  stopOutsideClickListener(): void {
    document.removeEventListener('click', this._outsideClickHandler, true)
    document.removeEventListener('touchend', this._outsideClickHandler, true)
  }

  closeAllDropdowns(): void {
    this.colorPickerOpen = false
    this.colorPickerProperty = undefined
    this.colorPickerFeatureId = undefined
    this.strokeStyleOpen = false
    this.strokeStyleProperty = undefined
    this.strokeStyleFeatureId = undefined
    this.strokeEditorOpen = false
    this.strokeEditorFeatureId = undefined
    this.markerEditorOpen = false
    this.markerEditorFeatureId = undefined
    this.labelEditorOpen = false
    this.labelEditorFeatureId = undefined
    this.tagEditorOpen = false
    this.tagEditorFeatureId = undefined
    this.layerPickerOpen = false
    this.layerPickerFeatureId = undefined
  }

  handleOpenStyleEditor(event: CustomEvent): void {
    const { featureId, styleProperty, feature, buttonRect } = event.detail
    const style = feature?.style ?? {}

    if (styleProperty === 'layer') {
      if (this.layerPickerOpen) {
        this.closeLayerPicker()
        return
      }
      this.positionDropdown(buttonRect)
      this.closeAllDropdowns()
      this.layerPickerFeatureId = featureId
      this.layerPickerOpen = true
      this.startOutsideClickListener()
      this.callbacks.onStateChanged()
      return
    }

    if (styleProperty === 'tags') {
      if (this.tagEditorOpen) {
        this.closeTagEditor()
        return
      }
      this.positionDropdown(buttonRect)
      this.closeAllDropdowns()
      this.tagEditorFeatureId = featureId
      this.tagEditorTags = feature?.tags ?? []
      this.tagEditorOpen = true
      this.startOutsideClickListener()
      this.callbacks.onStateChanged()
      return
    }

    if (styleProperty === 'label') {
      if (this.labelEditorOpen) {
        this.closeLabelEditor()
        return
      }
      this.positionDropdown(buttonRect)
      this.closeAllDropdowns()
      this.labelEditorFeatureId = featureId
      this.labelEditorText = feature?.label?.text ?? feature?.name ?? ''
      this.labelEditorColor = feature?.label?.fontColor ?? '#ffffff'
      this.labelEditorVisible = feature?.label?.visible ?? true
      this.labelEditorVerticalAlign = feature?.label?.verticalAlign ?? 'bottom'
      this.labelEditorHorizontalAlign = feature?.label?.horizontalAlign ?? 'center'
      this.labelEditorFontSize = feature?.label?.fontSize ?? 11
      this.labelEditorFontFamily = feature?.label?.fontFamily ?? 'sans-serif'
      this.labelEditorOpen = true
      this.startOutsideClickListener()
      this.callbacks.onStateChanged()
      return
    }

    if (styleProperty === 'marker') {
      if (this.markerEditorOpen) {
        this.closeMarkerEditor()
        return
      }
      this.positionDropdown(buttonRect)
      this.closeAllDropdowns()
      this.markerEditorFeatureId = featureId
      this.markerEditorType = style.markerType ?? 'circle'
      this.markerEditorIcon = style.markerIcon ?? ''
      this.markerEditorSize = pxToMarkerSize(style.markerSize ?? 12)
      this.markerEditorPosition = style.markerPosition ?? 'center'
      this.markerEditorOpen = true
      this.startOutsideClickListener()
      this.callbacks.onStateChanged()
      return
    }

    if (styleProperty === 'lineStroke' || styleProperty === 'stroke') {
      if (this.strokeEditorOpen && this.strokeEditorIsLine === (styleProperty === 'lineStroke')) {
        this.closeStrokeEditor()
        return
      }
      this.positionDropdown(buttonRect)
      this.closeAllDropdowns()
      const isLine = styleProperty === 'lineStroke'
      this.strokeEditorIsLine = isLine
      this.strokeEditorFeatureId = featureId
      this.strokeEditorColor = style[isLine ? 'lineColor' : 'strokeColor'] ?? '#3f5a7a'
      this.strokeEditorOpacity = style[isLine ? 'lineOpacity' : 'strokeOpacity'] ?? 1
      this.strokeEditorWidth = style[isLine ? 'lineWidth' : 'strokeWidth'] ?? (isLine ? 3 : 2)
      this.strokeEditorPattern = style[isLine ? 'linePattern' : 'strokePattern'] ?? 'solid'
      this.strokeEditorOpen = true
      this.startOutsideClickListener()
      this.callbacks.onStateChanged()
      return
    }

    if (styleProperty === 'linePattern' || styleProperty === 'strokePattern') {
      if (this.strokeStyleOpen && this.strokeStyleProperty === styleProperty) {
        this.closeStrokeStyle()
        return
      }
      this.positionDropdown(buttonRect)
      this.closeAllDropdowns()
      this.strokeStyleFeatureId = featureId
      this.strokeStyleProperty = styleProperty
      this.strokeStyleSelected = style[styleProperty] ?? 'solid'
      this.strokeStyleOpen = true
      this.startOutsideClickListener()
      this.callbacks.onStateChanged()
      return
    }

    if (this.colorPickerOpen && this.colorPickerProperty === styleProperty) {
      this.closeColorPicker()
      return
    }
    this.positionDropdown(buttonRect)
    this.closeAllDropdowns()
    this.colorPickerFeatureId = featureId
    this.colorPickerProperty = styleProperty

    const opacityMap: Record<string, string> = {
      fillColor: 'fillOpacity',
      pointColor: 'pointOpacity',
    }
    const defaultColors: Record<string, string> = {
      fillColor: '#3f5a7a',
      pointColor: '#3f5a7a',
    }
    this.colorPickerColor = style[styleProperty] ?? defaultColors[styleProperty] ?? '#3f5a7a'
    const opacityKey = opacityMap[styleProperty]
    this.colorPickerOpacity = opacityKey && style[opacityKey] !== undefined ? style[opacityKey] : 1
    if (styleProperty === 'fillColor') {
      this.colorPickerPattern = style.fillPattern ?? 'solid'
    }
    this.colorPickerOpen = true
    this.startOutsideClickListener()
    this.callbacks.onStateChanged()
  }

  async handleColorChanged(event: CustomEvent): Promise<void> {
    const { color, opacity } = event.detail
    if (!this.colorPickerFeatureId || !this.colorPickerProperty) {
      return
    }

    this.colorPickerColor = color
    this.colorPickerOpacity = opacity

    const opacityMap: Record<string, string> = {
      lineColor: 'lineOpacity',
      strokeColor: 'strokeOpacity',
      fillColor: 'fillOpacity',
      pointColor: 'pointOpacity',
    }

    const styleUpdates: Record<string, string | number | number[]> = {
      [this.colorPickerProperty]: color,
    }
    const opacityKey = opacityMap[this.colorPickerProperty]
    if (opacityKey) {
      styleUpdates[opacityKey] = opacity
    }

    const updatedFeature = await tripFeatureManager.updateFeatureStyle(
      this.colorPickerFeatureId,
      styleUpdates
    )
    if (!updatedFeature) {
      return
    }

    const shapeComponent = this.callbacks.getShapeComponent(this.colorPickerFeatureId)
    if (shapeComponent) {
      shapeComponent.feature = updatedFeature
      shapeComponent.refreshStyle()
    }

    await this.callbacks.updateSelectedFeatures()
  }

  async handleFillPatternChanged(event: CustomEvent): Promise<void> {
    const { pattern } = event.detail
    if (!this.colorPickerFeatureId) {
      return
    }

    this.colorPickerPattern = pattern

    const updatedFeature = await tripFeatureManager.updateFeatureStyle(this.colorPickerFeatureId, {
      fillPattern: pattern,
    })
    if (!updatedFeature) {
      return
    }

    const shapeComponent = this.callbacks.getShapeComponent(this.colorPickerFeatureId)
    if (shapeComponent) {
      shapeComponent.feature = updatedFeature
      shapeComponent.refreshStyle()
    }

    await this.callbacks.updateSelectedFeatures()
  }

  closeColorPicker(): void {
    this.colorPickerOpen = false
    this.colorPickerProperty = undefined
    this.colorPickerFeatureId = undefined
    if (!this.isAnyDropdownOpen) {
      this.stopOutsideClickListener()
    }
    this.callbacks.onStateChanged()
  }

  async handleStrokeStyleChanged(event: CustomEvent): Promise<void> {
    const { pattern, dashArray } = event.detail
    if (!this.strokeStyleFeatureId || !this.strokeStyleProperty) {
      return
    }

    this.strokeStyleSelected = pattern

    const dashArrayKey =
      this.strokeStyleProperty === 'linePattern' ? 'lineDashArray' : 'strokeDashArray'
    const dashArrayValue = dashArray ? dashArray.split(',').map(Number) : []

    const styleUpdates: Record<string, string | number | number[]> = {
      [this.strokeStyleProperty]: pattern,
      [dashArrayKey]: dashArrayValue,
    }

    const updatedFeature = await tripFeatureManager.updateFeatureStyle(
      this.strokeStyleFeatureId,
      styleUpdates
    )
    if (!updatedFeature) {
      return
    }

    const shapeComponent = this.callbacks.getShapeComponent(this.strokeStyleFeatureId)
    if (shapeComponent) {
      shapeComponent.feature = updatedFeature
      shapeComponent.refreshStyle()
    }

    await this.callbacks.updateSelectedFeatures()
  }

  closeStrokeStyle(): void {
    this.strokeStyleOpen = false
    this.strokeStyleProperty = undefined
    this.strokeStyleFeatureId = undefined
    if (!this.isAnyDropdownOpen) {
      this.stopOutsideClickListener()
    }
    this.callbacks.onStateChanged()
  }

  async handleStrokeEditorColorChanged(event: CustomEvent): Promise<void> {
    const { color, opacity } = event.detail
    if (!this.strokeEditorFeatureId) {
      return
    }
    this.strokeEditorColor = color
    this.strokeEditorOpacity = opacity
    const colorProp = this.strokeEditorIsLine ? 'lineColor' : 'strokeColor'
    const opacityProp = this.strokeEditorIsLine ? 'lineOpacity' : 'strokeOpacity'
    const styleUpdates: Record<string, string | number | number[]> = {
      [colorProp]: color,
      [opacityProp]: opacity,
    }
    const updatedFeature = await tripFeatureManager.updateFeatureStyle(
      this.strokeEditorFeatureId,
      styleUpdates
    )
    if (!updatedFeature) {
      return
    }
    const shapeComponent = this.callbacks.getShapeComponent(this.strokeEditorFeatureId)
    if (shapeComponent) {
      shapeComponent.feature = updatedFeature
      shapeComponent.refreshStyle()
    }
    await this.callbacks.updateSelectedFeatures()
  }

  async handleStrokeEditorWidthChanged(event: CustomEvent): Promise<void> {
    const { width } = event.detail
    if (!this.strokeEditorFeatureId) {
      return
    }
    this.strokeEditorWidth = width
    const widthProp = this.strokeEditorIsLine ? 'lineWidth' : 'strokeWidth'
    const styleUpdates: Record<string, string | number | number[]> = { [widthProp]: width }
    const updatedFeature = await tripFeatureManager.updateFeatureStyle(
      this.strokeEditorFeatureId,
      styleUpdates
    )
    if (!updatedFeature) {
      return
    }
    const shapeComponent = this.callbacks.getShapeComponent(this.strokeEditorFeatureId)
    if (shapeComponent) {
      shapeComponent.feature = updatedFeature
      shapeComponent.refreshStyle()
    }
    await this.callbacks.updateSelectedFeatures()
  }

  async handleStrokeEditorPatternChanged(event: CustomEvent): Promise<void> {
    const { pattern, dashArray } = event.detail
    if (!this.strokeEditorFeatureId) {
      return
    }
    this.strokeEditorPattern = pattern
    const patternProp = this.strokeEditorIsLine ? 'linePattern' : 'strokePattern'
    const dashArrayProp = this.strokeEditorIsLine ? 'lineDashArray' : 'strokeDashArray'
    const dashArrayValue = dashArray ? dashArray.split(',').map(Number) : []
    const styleUpdates: Record<string, string | number | number[]> = {
      [patternProp]: pattern,
      [dashArrayProp]: dashArrayValue,
    }
    const updatedFeature = await tripFeatureManager.updateFeatureStyle(
      this.strokeEditorFeatureId,
      styleUpdates
    )
    if (!updatedFeature) {
      return
    }
    const shapeComponent = this.callbacks.getShapeComponent(this.strokeEditorFeatureId)
    if (shapeComponent) {
      shapeComponent.feature = updatedFeature
      shapeComponent.refreshStyle()
    }
    await this.callbacks.updateSelectedFeatures()
  }

  closeStrokeEditor(): void {
    this.strokeEditorOpen = false
    this.strokeEditorFeatureId = undefined
    if (!this.isAnyDropdownOpen) {
      this.stopOutsideClickListener()
    }
    this.callbacks.onStateChanged()
  }

  async handleMarkerChanged(event: CustomEvent): Promise<void> {
    const { markerType, markerIcon, markerSize, markerPosition } = event.detail
    if (!this.markerEditorFeatureId) {
      return
    }

    this.markerEditorType = markerType
    this.markerEditorIcon = markerIcon
    this.markerEditorSize = pxToMarkerSize(markerSize)
    this.markerEditorPosition = markerPosition

    const updatedFeature = await tripFeatureManager.updateFeatureStyle(this.markerEditorFeatureId, {
      markerType,
      markerIcon,
      markerSize,
      markerPosition,
    })
    if (!updatedFeature) {
      return
    }

    const shapeComponent = this.callbacks.getShapeComponent(this.markerEditorFeatureId)
    if (shapeComponent) {
      shapeComponent.feature = updatedFeature
      shapeComponent.refreshStyle()
    }

    await this.callbacks.updateSelectedFeatures()
  }

  closeMarkerEditor(): void {
    this.markerEditorOpen = false
    this.markerEditorFeatureId = undefined
    if (!this.isAnyDropdownOpen) {
      this.stopOutsideClickListener()
    }
    this.callbacks.onStateChanged()
  }

  async handleLabelChanged(event: CustomEvent): Promise<void> {
    const { text, fontColor, visible, verticalAlign, horizontalAlign, fontSize, fontFamily } =
      event.detail
    if (!this.labelEditorFeatureId) {
      return
    }

    this.labelEditorText = text
    this.labelEditorColor = fontColor
    this.labelEditorVisible = visible
    this.labelEditorVerticalAlign = verticalAlign
    this.labelEditorHorizontalAlign = horizontalAlign
    this.labelEditorFontSize = fontSize
    this.labelEditorFontFamily = fontFamily

    const label: FeatureLabel = {
      text,
      position: 'center',
      fontColor,
      visible,
      fontSize,
      fontFamily,
      verticalAlign,
      horizontalAlign,
    }

    await tripFeatureManager.addFeatureLabel(this.labelEditorFeatureId, label)
    await db.features.update(this.labelEditorFeatureId, { name: text })

    const updatedFeature = await tripFeatureManager.getFeature(this.labelEditorFeatureId)
    if (!updatedFeature) {
      return
    }

    const shapeComponent = this.callbacks.getShapeComponent(this.labelEditorFeatureId)
    if (shapeComponent) {
      shapeComponent.feature = updatedFeature
      shapeComponent.refreshLabel()
    }

    await this.callbacks.updateSelectedFeatures()
  }

  closeLabelEditor(): void {
    this.labelEditorOpen = false
    this.labelEditorFeatureId = undefined
    if (!this.isAnyDropdownOpen) {
      this.stopOutsideClickListener()
    }
    this.callbacks.onStateChanged()
  }

  async handleTagsChanged(event: CustomEvent): Promise<void> {
    const { tags } = event.detail
    if (!this.tagEditorFeatureId) {
      return
    }

    this.tagEditorTags = tags

    // Tags live on the Feature model (db.features).
    await db.features.update(this.tagEditorFeatureId, { tags })

    await this.callbacks.updateSelectedFeatures()
  }

  closeTagEditor(): void {
    this.tagEditorOpen = false
    this.tagEditorFeatureId = undefined
    if (!this.isAnyDropdownOpen) {
      this.stopOutsideClickListener()
    }
    this.callbacks.onStateChanged()
  }

  closeLayerPicker(): void {
    this.layerPickerOpen = false
    this.layerPickerFeatureId = undefined
    if (!this.isAnyDropdownOpen) {
      this.stopOutsideClickListener()
    }
    this.callbacks.onStateChanged()
  }

  async handleLayerSelected(event: CustomEvent): Promise<void> {
    const { layerId, featureId } = event.detail
    if (!layerId) {
      return
    }
    if (featureId) {
      await layerGroupManager.moveFeatureToLayer(featureId, layerId)
    } else {
      for (const f of this.callbacks.getSelectedFeatures()) {
        await layerGroupManager.moveFeatureToLayer(f.id, layerId)
      }
    }
    layerGroupManager.setLastUsedLayer(layerId)
    this.closeLayerPicker()
    await this.callbacks.updateSelectedFeatures()
  }

  closeAllAndStopListening(): void {
    this.closeAllDropdowns()
    this.stopOutsideClickListener()
    this.callbacks.onStateChanged()
  }

  /** Close dropdowns that are relevant when selection is cleared */
  clearSelectionDropdowns(): void {
    this.colorPickerOpen = false
    this.strokeStyleOpen = false
    this.strokeEditorOpen = false
    this.layerPickerOpen = false
    this.stopOutsideClickListener()
    this.callbacks.onStateChanged()
  }

  dispose(): void {
    this.stopOutsideClickListener()
    this.closeAllDropdowns()
  }
}
